/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

function SidenavCollapse({ icon, name, active, hasSubmenu, isOpen, isSubmenu, onClick, ...rest }) {
  const [controller] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    purpleSidenav,
    darkMode,
    sidenavColor,
  } = controller;

  const handleClick = (e) => {
    if (hasSubmenu && onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <ListItem 
      component="li" 
      sx={{ 
        padding: 0,
        cursor: hasSubmenu ? 'pointer' : 'default',
        listStyle: 'none', 
      }}
    >
      <MDBox
        {...rest}
        onClick={handleClick}
        sx={(theme) => ({
          ...collapseItem(theme, {
            active,
            transparentSidenav,
            whiteSidenav,
            purpleSidenav,
            darkMode,
            sidenavColor,
            isSubmenu,
          }),
          cursor: hasSubmenu ? 'pointer' : 'default',
          userSelect: 'none',
          width: '100%',
          '&:hover': {
            backgroundColor: hasSubmenu ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
          }
        })}
      >
        <ListItemIcon
          sx={(theme) =>
            collapseIconBox(theme, {
              transparentSidenav,
              whiteSidenav,
              purpleSidenav,
              darkMode,
              active,
            })
          }
        >
          {typeof icon === "string" ? (
            <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
          ) : (
            icon
          )}
        </ListItemIcon>

        <ListItemText
          primary={name}
          sx={(theme) => ({
            ...collapseText(theme, {
              miniSidenav,
              transparentSidenav,
              whiteSidenav,
              purpleSidenav,
              active,
            }),
          })}
        />

        {/* Icono de expansión para submenus */}
        {hasSubmenu && (
          <Icon 
            sx={{ 
              color: "inherit", 
              fontSize: "1rem",
              marginLeft: "auto",
              pointerEvents: "none"
            }}
          >
            {isOpen ? "expand_less" : "expand_more"}
          </Icon>
        )}
      </MDBox>
    </ListItem>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
  hasSubmenu: false,
  isOpen: false,
  isSubmenu: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  hasSubmenu: PropTypes.bool,
  isOpen: PropTypes.bool,
  isSubmenu: PropTypes.bool,
  onClick: PropTypes.func,
};

export default SidenavCollapse;